import { useEffect, useState, RefObject } from 'react';

interface ScrollProgressOptions {
  threshold?: number;
}

export function useScrollProgress(
  containerRef: RefObject<HTMLElement | null>,
  options: ScrollProgressOptions = {}
): number {
  const [progress, setProgress] = useState(0);
  const { threshold = 0 } = options;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const rect = container.getBoundingClientRect();
      const containerTop = rect.top;
      const containerHeight = rect.height;
      const viewportHeight = window.innerHeight;

      // Calculate how far through the container we've scrolled
      // Progress 0 = container just entering viewport from bottom
      // Progress 1 = container just leaving viewport from top
      const scrollStart = viewportHeight - threshold;
      const scrollEnd = -containerHeight + threshold;
      const scrollRange = scrollStart - scrollEnd;
      const currentPosition = scrollStart - containerTop;

      const rawProgress = currentPosition / scrollRange;
      const clampedProgress = Math.max(0, Math.min(1, rawProgress));

      setProgress(clampedProgress);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [containerRef, threshold]);

  return progress;
}

export function useInView(
  ref: RefObject<HTMLElement | null>,
  options: { threshold?: number; once?: boolean } = {}
): boolean {
  const [isInView, setIsInView] = useState(false);
  const { threshold = 0.1, once = false } = options;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (once) {
            observer.unobserve(element);
          }
        } else if (!once) {
          setIsInView(false);
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, threshold, once]);

  return isInView;
}
